import React, {useEffect, useState} from 'react';
import {useEnvironment, useExperiments} from '@wix/yoshi-flow-editor';
import {Header} from '../Header/Header';
import {ContinueShoppingButton} from '../Header/ContinueShoppingButton';
import {HeaderSlot} from '../Header/HeaderSlot';
import {AsideSummary} from '../Summary/AsideSummary/AsideSummary';
import {FastFlow} from '../FastFlow/FastFlow';
import {Footer} from '../Footer/Footer';
import {useControllerProps} from '../Widget/ControllerContext';
import {TPAComponentsProvider} from '@wix/wix-ui-tpa/cssVars';
import {FullFlow} from '../FullFlow/FullFlow';
import {SummaryLocation} from '../../../types/checkoutApp.types';
import {getDirValue} from '../../../domain/utils/getDirValue';
import {Layout} from '../../../common/components/Layout/Layout';
import {pageTheme} from '../../../common/components/WithThemeOverride/pageTheme';
import {CheckoutErrorState} from '../CheckoutErrorState/CheckoutErrorState';
import {NoOnlineOrdersMessage} from '../NoOnlineOrdersMessage/NoOnlineOrdersMessage';
import {SPECS} from '../../../common/constants';
import {useReloadOnPageshow} from '../Hooks/useReloadOnPageshow';
import {useRegisterWebMcpTools} from '../Hooks/useRegisterWebMcpTools';

export enum CheckoutAppDataHook {
  root = 'CheckoutAppDataHook.root',
  footer = 'CheckoutAppDataHook.footer',
  header = 'CheckoutAppDataHook.header',
  summary = 'CheckoutAppDataHook.summary',
}

const CheckoutApp = () => {
  const {failedToFetch, isLoading, checkoutStore, isOneColumnView, checkoutSettingsStore, navigationStore} =
    useControllerProps();
  const {isRTL, isMobile} = useEnvironment();
  const {experiments} = useExperiments();
  const handleCheckoutErrorState = experiments.enabled(SPECS.HandleCheckoutErrorStates);
  const [ownerDismissedPremiumError, setOwnerDismissedPremiumError] = useState(false);
  const hasNoPremium = !isLoading && !checkoutSettingsStore?.isPremium;
  const hasOldPremiumError = !handleCheckoutErrorState && hasNoPremium && !navigationStore?.isOwnerOrPreview;
  const cannotShip = !isLoading && checkoutSettingsStore?.cannotShip && checkoutStore?.cart?.hasShippableItems;
  const showNoPremiumError = hasNoPremium && !ownerDismissedPremiumError;
  const shouldShowNoOnlineOrdersMessage = (cannotShip || showNoPremiumError) && handleCheckoutErrorState;
  const hasError = failedToFetch || hasOldPremiumError;
  const FlowComponent = checkoutStore?.isFastFlow ? FastFlow : FullFlow;

  useEffect(
    () => {
      if (isLoading) {
        return;
      }

      checkoutStore.checkoutPageFinishLoading({
        hasPremiumError: hasNoPremium,
        cannotShip,
      });
    },
    /* eslint-disable react-hooks/exhaustive-deps*/ [isLoading]
  );

  useReloadOnPageshow();
  useRegisterWebMcpTools();

  return (
    <div style={pageTheme} className={isMobile ? 'mobile-view' : undefined}>
      <TPAComponentsProvider value={{rtl: isRTL}}>
        <Layout
          dataHook={CheckoutAppDataHook.root}
          isLoading={isLoading}
          showErrorState={hasError}
          isOneColumnView={isOneColumnView}
          dir={getDirValue(isRTL)}
          Header={() => (
            <Header
              dataHook={CheckoutAppDataHook.header}
              checkoutSettings={checkoutSettingsStore.checkoutSettings}
              isOneColumnView={isOneColumnView}
              continueShoppingUrl={navigationStore?.continueShoppingUrl}
              clickOnContinueShopping={navigationStore?.clickOnContinueShopping}
              HeaderSuffix={ContinueShoppingButton}
              HeaderAfter={HeaderSlot}
            />
          )}
          Aside={() => <AsideSummary dataHook={CheckoutAppDataHook.summary} location={SummaryLocation.checkoutApp} />}
          ErrorState={() => <CheckoutErrorState hasPremiumError={hasOldPremiumError} />}
          Footer={() => <Footer dataHook={CheckoutAppDataHook.footer} />}>
          {shouldShowNoOnlineOrdersMessage ? (
            <NoOnlineOrdersMessage onDismissNoPremiumError={() => setOwnerDismissedPremiumError(true)} />
          ) : (
            <FlowComponent />
          )}
        </Layout>
      </TPAComponentsProvider>
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default CheckoutApp;
